import {
    fetchContact,
    fetchEditableContact,
} from 'flow/actions/contact-actions';
import { updateEditableContactField } from 'flow/actions/contact-editable-actions';
import ContactEditField from 'flow/components/contact-components/ContactEditField';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectEditableContactValueSimple,
    selectEditableContactValueIsEditable,
    selectHasEditableContactFields,
} from 'flow/selectors/contact-selectors';
import { selectExtensionContactKey } from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { useEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

const General = () => {
    const history = useHistory();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const contactKey = useAppSelector((state) =>
        selectExtensionContactKey(state, extensionKey),
    );
    const firstnameIsEditable = useAppSelector((state) =>
        selectEditableContactValueIsEditable(state, contactKey, 'firstname'),
    );
    const firstnameFromStore = useAppSelector((state) =>
        selectEditableContactValueSimple(state, contactKey, 'firstname'),
    );
    const hasEditableContactFields = useAppSelector((state) =>
        selectHasEditableContactFields(state, contactKey),
    );
    const [hasFetchedEditableContact, setHasFetchedEditableContact] =
        useState(false);
    const [name, setName] = useState(firstnameFromStore ?? '');

    useEffect(() => {
        if (contactKey) {
            Promise.all([
                dispatch(fetchContact(contactKey)),
                dispatch(fetchEditableContact(contactKey)),
            ])
                .then(() => {
                    setHasFetchedEditableContact(true);
                })
                .catch((error) => {
                    // Optionally handle error, e.g. log or show notification
                    console.error('Error fetching contact data:', error);
                });
        }
    }, [dispatch, contactKey]);

    useEffect(() => {
        if (!hasFetchedEditableContact) return;
        setName(firstnameFromStore);
    }, [hasFetchedEditableContact, firstnameFromStore]);

    if (!hasEditableContactFields) {
        return null;
    }

    const handleSubmit = (newName: string) => {
        dispatch(
            updateEditableContactField(
                contactKey,
                'firstname',
                newName,
                extensionKey,
            ),
        );
    };

    return (
        <Page
            header={i18next.t('ai-coworker.settings.general')}
            onClickBack={() => {
                history.push(
                    generatePath(
                        routes['pbx.aicoworker.extensionKey.settings'],
                        {
                            extensionKey,
                        },
                    ),
                );
            }}
        >
            <div className="flex flex-col gap-y-6">
                <ContactEditField
                    key="firstname"
                    field_key="firstname"
                    field_value={name}
                    field_func={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setName(event.target.value)
                    }
                    label={i18next.t('settings.profile.name_header')}
                    disableEdit={!firstnameIsEditable}
                />
            </div>

            <div className="mt-12 flex justify-end">
                <Button
                    color="primary"
                    onClick={() => handleSubmit(name)}
                    disabled={firstnameFromStore === name}
                >
                    {i18next.t('navigate.save')}
                </Button>
            </div>
        </Page>
    );
};

export default General;
